import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, FileText, Play, Images } from "lucide-react";
import { CATEGORY_LABEL } from "@/lib/api";
import { EASE } from "./SectionHead";
import { BlurImg } from "./BlurImg";

const ASPECT = { portrait: "aspect-[4/5]", square: "aspect-square", landscape: "aspect-[16/10]" };

export const ProjectCard = ({ project, index, className, shape }) => {
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  return (
    <motion.div className={className} initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.05 }} transition={{ duration: 0.9, delay: (index % 3) * 0.08, ease: EASE }}>
      <Link to={`/project/${project.id}`} data-testid={`project-card-${project.id}`} onMouseMove={onMove}
        className="group block clip-corner bg-[#F5A524]/25 hover:bg-[#F5A524] p-px transition-colors duration-300">
        <article data-testid={`project-card-frame-${project.id}`} data-shape={ASPECT[shape] ? shape : "portrait"} className={`relative clip-corner overflow-hidden bg-[#12131A] ${ASPECT[shape] || ASPECT.portrait}`}>
          <BlurImg src={project.cover_url} alt={project.title} testid={`project-card-image-${project.id}`} position={`${project.focus_x ?? 50}% ${project.focus_y ?? 50}%`}
            sizes="(min-width: 1280px) 31vw, (min-width: 768px) 46vw, 86vw" imgClassName="group-hover:scale-[1.06] group-hover:skew-x-[-1deg]" />
          <div className="absolute inset-x-0 bottom-0 h-1/2 min-h-[140px] bg-gradient-to-t from-black/90 via-black/40 to-transparent pointer-events-none" />
          <div className="absolute inset-0 spot opacity-0 group-hover:opacity-100 transition-opacity duration-300 mix-blend-screen" />
          <div className="absolute top-4 left-4 right-4 flex justify-between font-mono text-[11px] tracking-[0.25em]">
            <span className="bg-black/70 px-2 py-1 text-[#F5A524]">#{String(index + 1).padStart(2, "0")}</span>
            <span className="bg-black/70 border border-[#F5A524]/40 px-2 py-1 text-[#F5A524]">{CATEGORY_LABEL[project.category]}</span>
          </div>
          <div className="absolute bottom-0 inset-x-0 p-5 sm:p-6 flex items-end justify-between gap-4">
            <div>
              <h3 className="font-display font-black uppercase text-3xl sm:text-4xl leading-none text-white group-hover:text-[#F5A524] transition-colors line-clamp-2">{project.title}</h3>
              <p className="mt-2 font-mono text-[11px] tracking-[0.2em] text-zinc-300 flex items-center gap-3">
                {project.year}
                {project.pdf_url && <span className="flex items-center gap-1 text-[#F5A524]"><FileText size={12} /> PDF // READ</span>}
                {project.video_url && <span className="flex items-center gap-1 text-[#F5A524]"><Play size={12} /> VIDEO</span>}
                {project.images?.length > 0 && <span className="flex items-center gap-1 text-[#F5A524]"><Images size={12} /> +{project.images.length}</span>}
              </p>
            </div>
            <span className="shrink-0 w-11 h-11 grid place-items-center bg-[#F5A524] text-black translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
              <ArrowUpRight size={20} />
            </span>
          </div>
        </article>
      </Link>
    </motion.div>
  );
};
